import type { SchedulePerson, ScheduleSession } from "@/components/booking-schedule";
import type {
  ClientBookings,
  ClientHistoryEntry,
  ClientVisit,
  ClientVisitActions,
} from "@/features/booking/types/client.types";

export const BOOKINGS_TABS = ["upcoming", "calendar", "finished", "cancelled"] as const;
export type BookingsTab = (typeof BOOKINGS_TABS)[number];

/** Rows a list shows before "Show all (N)". */
export const LIST_LIMIT = 5;

/**
 * History statuses that mean the visit never took place: called off by either side, turned
 * down, or a request whose time passed with nobody answering it. A missed visit (no-show) did
 * have its time, so it counts as finished and carries a "Missed" label there.
 */
const DID_NOT_HAPPEN = new Set<ClientHistoryEntry["status"]>(["cancelled", "declined", "pending", "removed"]);

export interface BookingsView {
  /** Visits waiting on the client ("will you be there?", "pick a new time"), one per booking. */
  needsAnswer: ClientVisit[];
  /** Every other upcoming visit, soonest first. */
  comingUp: ClientVisit[];
  finished: ClientHistoryEntry[];
  cancelled: ClientHistoryEntry[];
}

/** The client owes an answer on this visit. The server says so; nothing is worked out here. */
export const needsAnswer = (visit: ClientVisit): boolean =>
  visit.actions.needsConfirmation || visit.actions.needsNewTime;

/**
 * Splits `/booking-client/bookings` into the three list tabs. Upcoming comes as the server sorted it
 * (soonest first); history keeps its newest-first order inside each tab.
 */
export const splitBookings = (data: ClientBookings | null | undefined): BookingsView => {
  const upcoming = data?.upcoming ?? [];
  const history = data?.history ?? [];
  // A weekly series asks "will you be there?" on every visit ahead. Only the soonest one of each
  // booking is lifted to the top; the later ones stay in the list and answer from their details.
  const lifted = new Map<string, string>();
  for (const visit of upcoming) {
    if (needsAnswer(visit) && !lifted.has(visit.bookingId)) lifted.set(visit.bookingId, visit.key);
  }
  const isLifted = (visit: ClientVisit) => lifted.get(visit.bookingId) === visit.key;
  return {
    needsAnswer: upcoming.filter(isLifted),
    comingUp: upcoming.filter((visit) => !isLifted(visit)),
    finished: history.filter((entry) => !DID_NOT_HAPPEN.has(entry.status)),
    cancelled: history.filter((entry) => DID_NOT_HAPPEN.has(entry.status)),
  };
};

/** The first rows of a long list, until "Show all" is asked for. */
export const limitList = <T>(items: T[], showAll: boolean, limit = LIST_LIMIT): T[] =>
  showAll ? items : items.slice(0, limit);

/** After `lateCancelAfter` a cancellation counts as late (the business's cancel window). */
export const isLateCancel = (visit: { lateCancelAfter: string }, now = new Date()): boolean =>
  now.getTime() > new Date(visit.lateCancelAfter).getTime();

/** What the way out of a visit is called: leaving an event, taking a request back, or cancelling. */
export type DismissKind = "leave_event" | "cancel_request" | "cancel_visit";

export const dismissKind = (visit: Pick<ClientVisit, "eventId" | "status">): DismissKind =>
  visit.eventId ? "leave_event" : visit.status === "pending" ? "cancel_request" : "cancel_visit";

export type VisitAction = "move" | "pick_time" | "withdraw_move" | "confirm_attendance" | "decline_attendance";

export interface VisitFooter {
  /** The destructive way out, alone on the left. It always opens the cancel step. */
  dismiss: boolean;
  secondary: VisitAction[];
  /** The one thing this visit is waiting for. */
  primary: VisitAction | null;
}

/**
 * The detail dialog's footer, straight from the server's `actions`. A visit the business asked
 * to move leads with "Pick a time"; one that asks "will you be there?" leads with the yes; a
 * move already asked for can only be taken back.
 */
export const visitFooter = (actions: ClientVisitActions): VisitFooter => {
  const dismiss = actions.canCancel;
  if (actions.needsNewTime) return { dismiss, secondary: [], primary: "pick_time" };
  if (actions.needsConfirmation) {
    return {
      dismiss,
      secondary: [...(actions.canMove ? (["move"] as const) : []), "decline_attendance"],
      primary: "confirm_attendance",
    };
  }
  if (actions.canWithdrawMove) return { dismiss, secondary: ["withdraw_move"], primary: null };
  return { dismiss, secondary: [], primary: actions.canMove ? "move" : null };
};

export type DetailTarget = { kind: "upcoming"; visit: ClientVisit } | { kind: "history"; entry: ClientHistoryEntry };

const sameInstant = (a: string, b: string) => new Date(a).getTime() === new Date(b).getTime();

/**
 * The visit a `?bookingId=` link points at. A series has many upcoming visits under one booking,
 * so `occurrence` picks the one; without it the soonest is shown.
 */
export const findTarget = (
  data: ClientBookings | null | undefined,
  bookingId: string | undefined,
  occurrence?: string,
): DetailTarget | null => {
  if (!data || !bookingId) return null;
  const visits = data.upcoming.filter((visit) => visit.bookingId === bookingId);
  const visit =
    (occurrence ? visits.find((candidate) => sameInstant(candidate.occurrenceStart, occurrence)) : null) ?? visits[0];
  if (visit) return { kind: "upcoming", visit };
  const entry = data.history.find((candidate) => candidate.bookingId === bookingId);
  return entry ? { kind: "history", entry } : null;
};

/** What "Book again" needs to find its way: the person, the service, or the event list. */
export interface BookAgainTarget {
  specialistId: string;
  sessionTypeId: string | null;
  eventId: string | null;
}

export interface VisitsCalendar {
  /** The specialists the visits are with, each once. */
  people: SchedulePerson[];
  sessions: ScheduleSession[];
}

const SCHEDULE_STATUSES: Record<ClientHistoryEntry["status"], ScheduleSession["status"] | null> = {
  confirmed: "confirmed",
  no_show: "no_show",
  cancelled: null,
  declined: null,
  pending: null,
  removed: null,
  // Still being paid for: not on the calendar until the payment confirms it.
  awaiting_payment: null,
};

/**
 * Every visit as a calendar session: all upcoming ones, and the past ones that took place (a
 * missed visit too, drawn as a no-show). Called-off visits stay in the Cancelled list only.
 * The card's bold line is who the visit is with: the specialist, or an event's hosts.
 */
export const visitsCalendar = (data: ClientBookings | null | undefined): VisitsCalendar => {
  const people = new Map<string, SchedulePerson>();
  const meet = (visit: ClientVisit | ClientHistoryEntry) => {
    if (!people.has(visit.specialistId)) {
      people.set(visit.specialistId, {
        id: visit.specialistId,
        name: visit.specialistName,
        avatarUrl: visit.specialistAvatarUrl,
      });
    }
  };

  const upcoming = (data?.upcoming ?? []).map((visit): ScheduleSession => {
    meet(visit);
    return {
      kind: "session",
      key: visit.key,
      bookingId: visit.bookingId,
      occurrenceStart: visit.occurrenceStart,
      startAt: new Date(visit.startAt),
      endAt: new Date(visit.endAt),
      personId: visit.specialistId,
      clientName: visit.eventId && visit.hostNames.length > 0 ? visit.hostNames.join(", ") : visit.specialistName,
      serviceName: visit.sessionTypeName,
      color: visit.color,
      status: visit.status === "pending" ? "pending" : "confirmed",
      // The server drops removed visits from the list, so "removed" never reaches the calendar.
      attendance: visit.attendance === "removed" ? "unknown" : visit.attendance,
      moveRequestedTo: visit.pendingMove ? new Date(visit.pendingMove.startAt) : null,
      isSeries: visit.isSeries,
      online: visit.online,
    };
  });

  const past = (data?.history ?? []).flatMap((entry): ScheduleSession[] => {
    const status = SCHEDULE_STATUSES[entry.status];
    if (!status) return [];
    meet(entry);
    return [
      {
        kind: "session",
        key: `${entry.bookingId}:${entry.startAt}`,
        bookingId: entry.bookingId,
        occurrenceStart: entry.startAt,
        startAt: new Date(entry.startAt),
        endAt: new Date(entry.endAt),
        personId: entry.specialistId,
        clientName: entry.specialistName,
        serviceName: entry.sessionTypeName,
        color: entry.color,
        status,
        attendance: status === "no_show" ? "no_show" : "unknown",
        moveRequestedTo: null,
        isSeries: false,
        online: entry.online,
      },
    ];
  });

  return { people: [...people.values()], sessions: [...past, ...upcoming] };
};
